// src/pages/connection/TableIntegrationSidePanelContent.tsx
import React, { useCallback, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ScrollableTable from '../../components/common/ScrollableTable';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import { apiGet } from '../../services/api';
import './TableIntegrationSidePanelContent.css';
import { logger } from '@/utils/logger';

// Definição da interface para itens da resposta da API, com estrutura aninhada 'dados'
interface ApiResponseItem {
  dados: {
    [key: string]: string | number | Date | null | undefined;
  };
}

// Definição da interface para registros na tabela, com atributos desestruturados
interface TableRecord {
  [key: string]: string | number | Date | null | undefined;
}

interface TableIntegrationSidePanelContentProps {
  tableName: string | null;
  onClose: () => void;
  fetchInitialData?: boolean;
}

const TableIntegrationSidePanelContent: React.FC<TableIntegrationSidePanelContentProps> = ({
  tableName: propTableName,
  onClose,
  fetchInitialData = true,
}) => {
  const { theme } = useTheme();
  const [columns, setColumns] = useState<
    { header: string; render: (item: TableRecord, index: number) => React.ReactNode }[]
  >([]);

  // ⚠️ ATENÇÃO — NÃO ALTERAR SEM ESTUDO + TESTE MANUAL REAL ⚠️
  //
  // Este componente é o MAIS COMPLEXO da app: tabela inteiramente DINÂMICA
  // (colunas geradas em runtime via setColumns a partir do 1º registro retornado).
  //
  // ARQUITETURA CRÍTICA:
  // 1. fetchTableData lê `propTableName` DIRETO do closure — NUNCA via ref imperativa.
  //    Razão: o usePaginatedFetch (pós-Fase 5.2.2, commit e002b71) tem cascata reativa
  //    `loadData → reset → useEffect interno [reset, enabled]` que DISPARA refetch
  //    quando fetchFunction muda. Se usar `useRef` para propTableName, a ref é
  //    sincronizada via useEffect da página que roda APÓS o useEffect interno do
  //    hook — race condition: cascata dispara antes da ref atualizar → primeiro
  //    fetch lê ref ainda null → setHasMore(false) → reset manual posterior fica
  //    bloqueado por isFetchingRef.current=true. SidePanel mostra "Nenhum registro
  //    encontrado" mesmo com tabela populada no banco. (Bug debugado em 2026-05-12.)
  //
  // 2. `enabled: fetchInitialData && !!propTableName` no usePaginatedFetch — impede
  //    fetch quando propTableName=null (SidePanel ainda fechado). Sem isso, o mount
  //    inicial dispara fetch vazio que faz setHasMore(false) e impede o refetch
  //    automático quando user clica num card.
  //
  // 3. NÃO adicionar setState dentro de fetchTableData (ex: setIsFetching) — isso
  //    causa loop infinito via cascata reativa: setState → re-render → fetchFn
  //    useCallback recriado → loadData/reset recriados → useEffect interno dispara
  //    novo fetch → ... O isFetchingRef interno do usePaginatedFetch já protege
  //    contra duplo-fetch. (Padrão consistente com hooks 4.x/5.x.)
  //
  // Se precisar mexer aqui: smoke manual obrigatório com DevTools Network aberto.
  // Esperado ao clicar num card: 1 GET para /v1/integracoes/tabelas/{nome}?page=0
  // retornando 200 + tabela renderiza com dados. Spec 05-integracoes cobre apenas
  // abertura do SidePanel, NÃO o fetch da tabela dinâmica.
  const fetchTableData = useCallback(
    async (page: number, _currentItems: TableRecord[]) => {
      if (!propTableName) {
        return { items: [], totalItems: 0 };
      }
      try {
        const endpoint = `/v1/integracoes/tabelas/${propTableName}?page=${page}&size=10`;
        logger.log('Enviando requisição para endpoint:', endpoint);
        const response = await apiGet(endpoint);
        logger.log('Resposta da API completa:', response.data);
        const data = Array.isArray(response.data.content)
          ? response.data.content.map((item: ApiResponseItem) => ({
              ...item.dados, // Desestrutura 'dados' para atributos diretos (ex.: data_integracao, cod_categoria)
            }))
          : [];
        logger.log('Dados processados:', data);

        // Gera colunas dinamicamente com base no primeiro registro na página 0
        if (page === 0 && data.length > 0) {
          const sampleRecord = data[0];
          const newColumns = Object.keys(sampleRecord).map((key) => ({
            header: key,
            render: (item: TableRecord, _index: number) => {
              const value = item[key]; // Acessa o atributo diretamente após desestruturação
              return value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
                ? 'N/A'
                : value instanceof Date
                ? value.toISOString()
                : value.toString();
            },
          }));
          setColumns(newColumns);
        }

        const newTotalItems = response.data.page?.totalElements || data.length;
        return { items: data, totalItems: newTotalItems };
      } catch (err) {
        logger.error('Erro ao carregar dados:', err);
        return { items: [], totalItems: 0 };
      }
    },
    [propTableName]
  );

  const { items, hasMore, isLoading, loadMore } = usePaginatedFetch<TableRecord>({
    fetchFunction: fetchTableData,
    pageSize: 10,
    uniqueKey: undefined, // Desativa filtro de duplicatas, permitindo acumulação de linhas repetidas
    initialPage: 0,
    enabled: fetchInitialData && !!propTableName, // Desabilita fetch quando propTableName null (SidePanel fechado)
  });

  return (
    <div className={`table-integration-sidepanel-content ${theme}`}>
      <CloseButtonSidePanel onClick={onClose} className="close-button-absolute" disabled={false} />
      <div className="title-table">
        <div className="facelet-h3">
          <h3>Visualizando Tabela de Integração: {propTableName || ''}</h3>
        </div>
        <div className="facelet-p">
          <p></p>
        </div>
      </div>
      <ScrollableTable
        data={items}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoading={isLoading}
        columns={columns}
        onSelectChange={() => {}}
        emptyMessage="Nenhum registro encontrado."
        showCheckboxes={false}
      />
      <div className="form-actions">
      </div>
    </div>
  );
};

export default TableIntegrationSidePanelContent;